import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import { memo, useMemo, useRef, useState } from 'react'
import { LoaderCircle, Pause, Play, Settings } from 'lucide-react'
import type {
  LearningAnswerOption,
  LearningContentItem,
  LearningLessonDetail,
  LearningQuestionItem,
} from '@services/firebase/learning'
import { useDashboardState } from '@features/dashboard'
import { selectPreferredLearningLessonDetails } from '@shared/media/audio/learning-audio-preference'
import { isAlphabetVocabulary } from './online-book-content/alphabet-audio'
import { useAlphabetAudioPlayer } from './online-book-content/use-alphabet-audio-player'
import {
  getAnswerOptionLabel,
  resolveLocalizedDisplayText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import {
  getLegacyCardWidth,
  getLegacyAudioPlaybackStatusMessage,
  getPlayableAudio,
  getVisibleAnswers,
  getVisibleContent,
  getVisibleQuestions,
  LegacyAudioWaveIcon,
  LegacyEmbeddedWaveIcon,
  LearningAudioWave,
  playAudio,
  resolveContentTitle,
  resolveContentTranslation,
  resolveLegacyVocabularyTranslation,
  useLegacyAudioPlayback,
} from '@features/learning/shared/online-book-content/primitives'
import {
  getOnlineBookBlocksInSourceOrder,
  resolveLegacyOnlineBookRendererTemplate,
} from '@features/learning/shared/online-book-template'
import { LegacyAudioSettingsPopover as LegacySharedAudioSettingsPopover } from '@shared/media/audio/legacy-audio-settings'
import {
  SafeHtml,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'


interface OnlineBookContentRendererProps {
  readonly blocks: LearningLessonDetail[]
  readonly locale: string
}

function LegacyBlockAudio({
  source,
}: {
  readonly source?: string
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLButtonElement | null>(null)
  const [changeSpeed, setChangeSpeed] = useState(false)

  if (!source) {
    return null
  }

  const handleToggleAudio = () => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    audio.currentTime = audio.currentTime || 0
    audio.playbackRate = playbackRate

    if (isPlaying) {
      audio.pause()
      return
    }

    playAudio(audio)
  }

  const handleChangeSpeed = (speed: number) => {
    setPlaybackRate(speed)
    setChangeSpeed(false)

    if (audioRef.current) {
      audioRef.current.playbackRate = speed
    }
  }

  return (
    <div className="legacyBlockAudioBox">
      <div className="legacyBlockAudioContent">
        <button
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          className="legacyBlockAudioIcon"
          onClick={handleToggleAudio}
          type="button"
        >
          {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
        </button>
        <div
          aria-hidden="true"
          className="legacyBlockAudioProgress"
          onClick={(event) => {
            const audio = audioRef.current

            if (!audio || !duration) {
              return
            }

            const bounds = event.currentTarget.getBoundingClientRect()
            audio.currentTime = ((event.clientX - bounds.left) / bounds.width) * duration
          }}
        >
          <span style={{ width: `${position * 100}%` }} />
        </div>
        <button
          aria-label="Audio speed"
          className={`legacyBlockAudioSetting ${settingsAnchor ? 'show' : ''}`}
          onClick={(event) => {
            if (settingsAnchor) {
              setSettingsAnchor(null)
              setChangeSpeed(false)
              return
            }

            setSettingsAnchor(event.currentTarget)
            setChangeSpeed(false)
          }}
          type="button"
        >
          <Settings aria-hidden="true" />
        </button>
      </div>
      <CachedLearningAudio
        onEnded={() => {
          setIsPlaying(false)
          setPosition(0)
        }}
        onLoadedData={() => {
          setDuration(audioRef.current?.duration ?? 0)
        }}
        onPause={() => {
          setIsPlaying(false)
        }}
        onPlay={() => {
          setIsPlaying(true)
        }}
        onTimeUpdate={() => {
          const audio = audioRef.current

          if (!audio?.duration) {
            return
          }

          setPosition(audio.currentTime / audio.duration)
        }}
        ref={audioRef}
        src={source}
      />
      <LegacySharedAudioSettingsPopover
        anchorEl={settingsAnchor}
        onClose={() => {
          setSettingsAnchor(null)
          setChangeSpeed(false)
        }}
        paperClassName="legacyAudioSettingsPopoverPaper"
        transitionDuration={0}
      >
        <div className="legacyAudioSettingsPanel legacyBlockAudioSettingsPanel">
          {changeSpeed ? (
            <div>
              <button
                className="legacyAudioSettingBack"
                onClick={() => {
                  setChangeSpeed(false)
                }}
                type="button"
              >
                Speed
              </button>
              <div className="legacyAudioSettingMenu">
                {[0.75, 1].map((speed) => (
                  <button
                    className={playbackRate === speed ? 'current' : ''}
                    key={speed}
                    onClick={() => {
                      handleChangeSpeed(speed)
                    }}
                    type="button"
                  >
                    {speed === 1 ? 'Normal' : `${speed}x`}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="legacyAudioSettingMenu">
              <button
                className="legacyAudioSettingDrill"
                onClick={() => {
                  setChangeSpeed(true)
                }}
                type="button"
              >
                <span>Speed</span>
                <span>{playbackRate === 1 ? 'Normal' : '0.75x'}</span>
              </button>
            </div>
          )}
        </div>
      </LegacySharedAudioSettingsPopover>
    </div>
  )
}

const LegacyVocabularyRow = memo(function LegacyVocabularyRow({
  item,
  locale,
}: {
  readonly item: LearningContentItem
  readonly locale: string
}) {
  const translation = resolveTranslatedText(item.translates, locale)
  const title = item.text ?? resolveLocalizedDisplayText(item.title, locale)
  const source = getPlayableAudio(item)
  const { audioProps, failureReason, isPlaying, playbackState, toggleAudio } = useLegacyAudioPlayback(source)
  const audioStatusMessage = getLegacyAudioPlaybackStatusMessage(playbackState, locale)

  if (!title && !translation) {
    return null
  }

  return (
    <div
      className="legacyVocabularyRow"
      data-audio-error={failureReason ?? undefined}
      data-audio-state={source ? playbackState : undefined}
      onClick={() => {
        toggleAudio()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggleAudio()
        }
      }}
      role={source ? 'button' : undefined}
      aria-busy={playbackState === 'starting' || undefined}
      tabIndex={source ? 0 : -1}
    >
      <div className={`legacyVocabularyColumn primary ${isPlaying ? 'isPlaying' : ''}`}>
        <div className="legacyVocabularyContent">
          <span>
            {title}
            {item.type ? <span className="legacyVocabularyType"> ({item.type})</span> : null}
            {source ? (
              <LegacyEmbeddedWaveIcon
                className="legacyVocabularyWave"
                isPlaying={isPlaying}
              />
            ) : null}
          </span>
        </div>
      </div>
      {translation ? (
        <div className={`legacyVocabularyColumn secondary ${isPlaying ? 'isPlaying' : ''}`}>
          <div className="legacyVocabularyContent">{translation}</div>
        </div>
      ) : null}
      {audioProps ? <CachedLearningAudio {...audioProps} /> : null}
      {audioStatusMessage ? (
        <span aria-live="polite" className="sr-only" role="status">
          {audioStatusMessage}
        </span>
      ) : null}
    </div>
  )
})

function LegacyPronouncingPracticeCard({
  item,
  width,
}: {
  readonly item: LearningContentItem
  readonly width: string
}) {
  const source = getPlayableAudio(item)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)

  const handleToggleAudio = () => {
    const audio = audioRef.current

    if (!audio || !source) {
      return
    }

    audio.currentTime = currentTime

    if (isPlaying) {
      audio.pause()
      return
    }

    playAudio(audio)
  }

  return (
    <div className="legacyPronouncingPracticeItem" style={{ width }}>
      <div
        className={`legacyPronouncingPracticeCard ${isPlaying ? 'playing' : ''}`}
        onClick={handleToggleAudio}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggleAudio()
          }
        }}
        role={source ? 'button' : undefined}
        tabIndex={source ? 0 : -1}
      >
        <span>{item.text}</span>
        {source && isPlaying ? (
          <span className="legacyPronouncingPracticePlayIcon audio-playing">
            <LegacyAudioWaveIcon />
          </span>
        ) : null}
      </div>
      {source ? (
        <CachedLearningAudio
          onEnded={() => {
            setCurrentTime(0)
          }}
          onPause={() => {
            setIsPlaying(false)
          }}
          onPlay={() => {
            setIsPlaying(true)
          }}
          onTimeUpdate={() => {
            setCurrentTime(audioRef.current?.currentTime ?? 0)
          }}
          ref={audioRef}
          src={source}
        />
      ) : null}
    </div>
  )
}

function LegacyPronouncingPracticeBlock({
  block,
  locale,
}: {
  readonly block: LearningLessonDetail
  readonly locale: string
}) {
  const content = getVisibleContent(block.content)
  const title = resolveLocalizedDisplayText(block.title, locale, block.text)
  const itemWidth =
    content.length <= 1 ? '100%' : content.length === 2 ? '50%' : '33.33%'

  return (
    <section className="legacyPronouncingPracticeBlock">
      {title ? <h2 className="legacyPronouncingPracticeTitle">{title}</h2> : null}
      <div className="legacyPronouncingPracticeGrid">
        {content.map((item, index) => (
          <LegacyPronouncingPracticeCard
            item={item}
            key={`${block.id}-pronouncing-${item.id ?? index}`}
            width={itemWidth}
          />
        ))}
      </div>
    </section>
  )
}

function resolveStructureFallbackIcon(type: string | undefined) {
  if (type === 'question') {
    return '?'
  }

  if (type === 'answer') {
    return '~'
  }

  return '!'
}

function LegacyBubbleLine({
  item,
  locale,
  structureMode,
}: {
  readonly item: LearningContentItem
  readonly locale: string
  readonly structureMode: boolean
}) {
  const source = getPlayableAudio(item)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const translation = resolveContentTranslation(item, locale)

  const handleToggleAudio = () => {
    const audio = audioRef.current

    if (!audio || !source) {
      return
    }

    audio.currentTime = currentTime

    if (isPlaying) {
      audio.pause()
      return
    }

    playAudio(audio)
  }

  const textBody = (
    <span className="legacyConversationContentText">
      {item.text ? <span>{item.text}</span> : null}
      {source ? (
        <LearningAudioWave className="legacyConversationWave" isPlaying={isPlaying} />
      ) : null}
    </span>
  )

  return (
    <div className={`legacyConversationBubble ${item.isRight ? 'right' : 'left'}`}>
      <div
        className={`legacyConversationBubbleButton ${isPlaying ? 'playing' : ''}`}
        onClick={handleToggleAudio}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggleAudio()
          }
        }}
        role={source ? 'button' : undefined}
        tabIndex={source ? 0 : -1}
      >
        <span className={`legacyConversationAvatar ${structureMode ? 'structure' : ''}`}>
          {item.avatar ?? item.image ? (
            <img alt="" src={item.avatar ?? item.image} />
          ) : structureMode ? (
            <span className="legacyConversationAvatarFallback">
              {resolveStructureFallbackIcon(item.type)}
            </span>
          ) : null}
        </span>
        <span className="legacyConversationContent">
          {translation ? (
            <TooltipProvider delayDuration={250}>
              <Tooltip>
                <TooltipTrigger asChild>{textBody}</TooltipTrigger>
                <TooltipContent side="bottom">
                  {translation}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            textBody
          )}
        </span>
      </div>
      {source ? (
        <CachedLearningAudio
          onEnded={() => {
            setCurrentTime(0)
          }}
          onPause={() => {
            setIsPlaying(false)
          }}
          onPlay={() => {
            setIsPlaying(true)
          }}
          onTimeUpdate={() => {
            setCurrentTime(audioRef.current?.currentTime ?? 0)
          }}
          ref={audioRef}
          src={source}
        />
      ) : null}
    </div>
  )
}

function LegacyConversationTemplateBlock({
  block,
  locale,
  structureMode,
}: {
  readonly block: LearningLessonDetail
  readonly locale: string
  readonly structureMode: boolean
}) {
  const content = getVisibleContent(block.content)
  const title = resolveLocalizedDisplayText(block.title, locale, block.text)

  return (
    <section className={`legacyConversationBlock ${structureMode ? 'legacyStructureBlock' : ''}`}>
      {structureMode && title ? <div className="legacyStructureTitle">{title}</div> : null}
      {block.featuredImage ? (
        <img alt={title ?? 'Conversation'} src={block.featuredImage} />
      ) : null}
      {content.map((item, index) => (
        <LegacyBubbleLine
          item={item}
          key={`${block.id}-${structureMode ? 'structure' : 'conversation'}-${item.id ?? index}`}
          locale={locale}
          structureMode={structureMode}
        />
      ))}
    </section>
  )
}

function LegacyHighlighterItem({
  item,
  locale,
}: {
  readonly item: LearningContentItem
  readonly locale: string
}) {
  const title = item.text
  const translation = resolveLegacyVocabularyTranslation(item, locale)
  const source = getPlayableAudio(item)
  const { audioProps, failureReason, isPlaying, playbackState, toggleAudio } = useLegacyAudioPlayback(source)
  const audioStatusMessage = getLegacyAudioPlaybackStatusMessage(playbackState, locale)

  if (!title && !translation) {
    return null
  }

  return (
    <div
      className={`legacyHighlighterItem ${isPlaying ? 'playing' : ''}`}
      data-audio-error={failureReason ?? undefined}
      data-audio-state={source ? playbackState : undefined}
      onClick={() => {
        toggleAudio()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggleAudio()
        }
      }}
      role={source ? 'button' : undefined}
      aria-busy={playbackState === 'starting' || undefined}
      tabIndex={source ? 0 : -1}
    >
      {title ? (
        <h3>
          {title}
          {source ? (
            <LegacyEmbeddedWaveIcon
              className="legacyHighlighterWave"
              isPlaying={isPlaying}
            />
          ) : null}
        </h3>
      ) : null}
      {translation ? <p>{translation}</p> : null}
      {audioProps ? <CachedLearningAudio {...audioProps} /> : null}
      {audioStatusMessage ? (
        <span aria-live="polite" className="sr-only" role="status">
          {audioStatusMessage}
        </span>
      ) : null}
    </div>
  )
}

function LegacyHighlighterBlock({
  content,
  locale,
}: {
  readonly content: readonly LearningContentItem[]
  readonly locale: string
}) {
  return (
    <section className="legacyHighlighterBlock">
      {content.map((item, index) => (
        <LegacyHighlighterItem
          item={item}
          key={`${item.id ?? index}-highlight`}
          locale={locale}
        />
      ))}
    </section>
  )
}

const LegacyVocabularyGridItem = memo(function LegacyVocabularyGridItem({
  item,
  itemCount,
  locale,
  withImages,
  playback,
}: {
  readonly item: LearningContentItem
  readonly itemCount: number
  readonly locale: string
  readonly withImages: boolean
  readonly playback?: Pick<ReturnType<typeof useLegacyAudioPlayback>, 'failureReason' | 'isPlaying' | 'playbackState' | 'toggleAudio'> & { readonly engine: 'WEB_AUDIO' | 'NATIVE' }
}) {
  const translation = resolveLegacyVocabularyTranslation(item, locale)
  const title = item.text ?? resolveLocalizedDisplayText(item.title, locale)
  const image = item.avatar ?? item.featuredImage ?? item.image
  const source = getPlayableAudio(item)
  const nativePlayback = useLegacyAudioPlayback(playback ? undefined : source)
  const { audioProps, failureReason, isPlaying, playbackState, toggleAudio } = playback
    ? { audioProps: null, ...playback }
    : nativePlayback
  const audioStatusMessage = getLegacyAudioPlaybackStatusMessage(playbackState, locale)

  return (
    <div
      className="legacyVocabularyGridCell"
      style={{ width: getLegacyCardWidth(itemCount) }}
    >
      <div
        className={`legacyVocabularyGridItem ${!withImages && isPlaying ? 'playing' : ''}`}
        data-audio-error={failureReason ?? undefined}
        data-audio-engine={playback?.engine}
        data-audio-state={source ? playbackState : undefined}
        onClick={() => {
          toggleAudio()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleAudio()
          }
        }}
        role={source ? 'button' : undefined}
        aria-busy={playbackState === 'starting' || undefined}
        tabIndex={source ? 0 : -1}
      >
        {withImages && image ? (
          <span className="legacyVocabularyGridImage">
            <img alt="" src={image} />
          </span>
        ) : null}
        <span className="legacyVocabularyGridTitle">
          {title}
          {translation ? (
            <span className="legacyVocabularyGridTranslation">{translation}</span>
          ) : null}
        </span>
        {source && playbackState === 'starting' && playback ? (
          <span aria-hidden="true" className="legacyVocabularyGridPlayIcon">
            <LoaderCircle className="size-4 animate-spin" />
          </span>
        ) : null}
        {source && isPlaying ? (
          <span className="legacyVocabularyGridPlayIcon">
            <LegacyEmbeddedWaveIcon className="legacyVocabularyGridWave" isPlaying />
          </span>
        ) : null}
      </div>
      {audioProps ? <CachedLearningAudio {...audioProps} /> : null}
      {audioStatusMessage ? (
        <span aria-live="polite" className={playbackState === 'failed' && playback ? 'mt-1 block text-xs' : 'sr-only'} role="status">
          {audioStatusMessage}
        </span>
      ) : null}
    </div>
  )
})

function AlphabetVocabularyGrid({
  content,
  locale,
}: {
  readonly content: readonly LearningContentItem[]
  readonly locale: string
}) {
  const { state, toggle, containerRef } = useAlphabetAudioPlayer(content.map(getPlayableAudio).filter((source): source is string => Boolean(source)))
  return (
    <div className="legacyVocabularyGrid" data-alphabet-audio="true" ref={containerRef}>
      {content.map((item, index) => {
        const key = `alphabet-${index}`
        const source = getPlayableAudio(item)
        const selected = state.key === key
        return (
          <LegacyVocabularyGridItem
            item={item}
            itemCount={content.length}
            key={key}
            locale={locale}
            withImages={false}
            playback={{
              engine: state.engine,
              failureReason: selected ? state.failureReason : null,
              isPlaying: selected && state.playbackState === 'playing',
              playbackState: selected ? state.playbackState : 'idle',
              toggleAudio: async () => { if (source) await toggle(key, source) },
            }}
          />
        )
      })}
    </div>
  )
}

function LegacyVocabularyGrid({
  content,
  locale,
  withImages,
}: {
  readonly content: readonly LearningContentItem[]
  readonly locale: string
  readonly withImages: boolean
}) {
  if (!withImages && isAlphabetVocabulary(content)) {
    return <AlphabetVocabularyGrid content={content} locale={locale} />
  }
  return (
    <div className={`legacyVocabularyGrid ${withImages ? 'withImages' : ''}`}>
      {content.map((item, index) => (
        <LegacyVocabularyGridItem
          item={item}
          itemCount={content.length}
          key={`${item.id ?? index}-grid`}
          locale={locale}
          withImages={withImages}
        />
      ))}
    </div>
  )
}

function LegacyQuestionList({
  locale,
  questions,
}: {
  readonly locale: string
  readonly questions: readonly LearningQuestionItem[] | undefined
}) {
  const visibleQuestions = getVisibleQuestions(questions)

  if (visibleQuestions.length === 0) {
    return null
  }

  return (
    <div className="legacyQuestionList">
      {visibleQuestions.map((question, index) => {
        const text = resolveLocalizedDisplayText(question.translate, locale) ?? question.text

        return text ? <p key={`${text}-${index}`}>{text}</p> : null
      })}
    </div>
  )
}

function LegacyListItem({
  item,
  locale,
}: {
  readonly item: LearningContentItem
  readonly locale: string
}) {
  const title = item.text ?? resolveLocalizedDisplayText(item.title, locale)
  const translation = resolveContentTranslation(item, locale)
  const source = getPlayableAudio(item)
  const { audioProps, isPlaying, toggleAudio } = useLegacyAudioPlayback(source)

  return (
    <li
      onClick={() => {
        toggleAudio()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggleAudio()
        }
      }}
      role={source ? 'button' : undefined}
      tabIndex={source ? 0 : -1}
    >
      <span className="title">
        {title}
        {source ? <LegacyEmbeddedWaveIcon className="legacyListWave" isPlaying={isPlaying} /> : null}
      </span>
      {translation ? <small className="translate">{translation}</small> : null}
      {audioProps ? <CachedLearningAudio {...audioProps} /> : null}
    </li>
  )
}

function LegacyAnswerList({
  answers,
  locale,
}: {
  readonly answers:
    | readonly (LearningAnswerOption | string)[]
    | LearningAnswerOption
    | string
    | undefined
  readonly locale: string
}) {
  const visibleAnswers = getVisibleAnswers(answers)

  if (visibleAnswers.length === 0) {
    return null
  }

  return (
    <div className="legacyAnswerList">
      {visibleAnswers.map((answer, index) => (
        <span key={`answer-${index}`}>{getAnswerOptionLabel(answer, locale)}</span>
      ))}
    </div>
  )
}

function LegacyMultipleChoiceBlock({
  block,
  locale,
}: {
  readonly block: LearningLessonDetail
  readonly locale: string
}) {
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null)
  const visibleAnswers = getVisibleAnswers(block.answers)
  const title = resolveLocalizedDisplayText(block.title, locale, block.text)

  return (
    <section className="legacyMultipleChoiceBlock">
      {title ? <h2>{title}</h2> : null}
      <LegacyBlockAudio source={getPlayableAudio(block)} />
      <LegacyQuestionList locale={locale} questions={block.questions} />
      {visibleAnswers.length > 0 ? (
        <div className={`legacyMultipleChoiceAnswers ${selectedAnswerIndex === null ? '' : 'answered'}`}>
          {visibleAnswers.map((answer, index) => {
            const isSelected = selectedAnswerIndex === index
            const isCorrect =
              typeof answer !== 'string' &&
              Boolean(answer.correct ?? answer.isRight ?? answer.result)

            return (
              <button
                className={`legacyMultipleChoiceAnswer ${
                  isSelected ? (isCorrect ? 'correct' : 'incorrect') : ''
                }`}
                key={`multiple-choice-${block.id}-${index}`}
                onClick={() => {
                  setSelectedAnswerIndex(index)
                }}
                type="button"
              >
                <span>{getAnswerOptionLabel(answer, locale)}</span>
                <small>{index + 1}</small>
              </button>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

function LegacyFallbackBlock({
  block,
  locale,
  template,
}: {
  readonly block: LearningLessonDetail
  readonly locale: string
  readonly template: string
}) {
  const visibleContent = getVisibleContent(block.content)
  const title = resolveLocalizedDisplayText(block.title, locale, block.text)

  return (
    <section className="legacyOnlineBookFallback">
      {title ? <h2>{title}</h2> : null}
      {block.text && block.text !== title ? <p>{block.text}</p> : null}
      {block.featuredImage ? (
        <img alt={title ?? 'Lesson media'} src={block.featuredImage} />
      ) : null}
      <LegacyBlockAudio source={getPlayableAudio(block)} />
      <LegacyQuestionList locale={locale} questions={block.questions} />
      <LegacyAnswerList answers={block.answers} locale={locale} />
      {visibleContent.map((item, index) =>
        item.data?.text ? (
          <div className="legacyQuestionList" key={`${block.id}-fallback-${item.id ?? index}`}>
            {resolveContentTitle(item, locale) ? <p>{resolveContentTitle(item, locale)}</p> : null}
            {resolveContentTranslation(item, locale) ? (
              <p>{resolveContentTranslation(item, locale)}</p>
            ) : null}
            <SafeHtml as="p" html={item.data.text} />
          </div>
        ) : (
          <LegacyVocabularyRow
            item={item}
            key={`${block.id}-fallback-${item.id ?? index}`}
            locale={locale}
          />
        ),
      )}
      <LegacyAnswerList answers={[template]} locale={locale} />
    </section>
  )
}

function renderBlock(block: LearningLessonDetail, locale: string) {
  const content = getVisibleContent(block.content)
  const title = resolveLocalizedDisplayText(block.title, locale, block.text)
  const template = resolveLegacyOnlineBookRendererTemplate(block)

  if (template === 'horizontal_rule') {
    return <hr className="legacyOnlineBookRule" />
  }

  if (template === 'heading') {
    return <div className="legacyOnlineBookHeading">{title}</div>
  }

  if (template === 'subtitle') {
    return <div className="legacyOnlineBookSubtitle">{title}</div>
  }

  if (template === 'image') {
    return block.featuredImage ? (
      <img
        alt={title ?? 'Lesson illustration'}
        className="legacyOnlineBookImage"
        src={block.featuredImage}
      />
    ) : null
  }

  if (template === 'blog') {
    const blogContent = block.content ?? []

    return (
      <section className="legacyOnlineBookBlog">
        {title ? <div className="legacyOnlineBookBlogTitle">{title}</div> : null}
        <LegacyBlockAudio source={getPlayableAudio(block)} />
        {blogContent.map((item, index) =>
          item.data?.text ? (
            <SafeHtml
              as="p"
              html={item.data.text}
              key={`${block.id}-blog-${index}`}
            />
          ) : null,
        )}
      </section>
    )
  }

  if (template === 'lists') {
    return (
      <section className="legacyOnlineBookListBlock">
        {title ? <h2>{title}</h2> : null}
        <LegacyBlockAudio source={getPlayableAudio(block)} />
        <ol>
          {content.map((item, index) => (
            <LegacyListItem
              item={item}
              key={`${block.id}-list-${index}`}
              locale={locale}
            />
          ))}
        </ol>
      </section>
    )
  }

  if (template === 'vocabularyWithImage' || template === 'vocabularyWithoutImage') {
    return (
      <section className="legacyVocabularyBlock">
        <LegacyVocabularyGrid
          content={content}
          locale={locale}
          withImages={template === 'vocabularyWithImage'}
        />
      </section>
    )
  }

  if (template === 'highlighter') {
    return <LegacyHighlighterBlock content={content} locale={locale} />
  }

  if (template === 'vocabulary') {
    return (
      <section className="legacyVocabularyBlock">
        {content.map((item, index) => (
          <LegacyVocabularyRow
            item={item}
            key={`${block.id}-vocabulary-${item.id ?? index}`}
            locale={locale}
          />
        ))}
      </section>
    )
  }

  if (template === 'pronouncing_practice') {
    return <LegacyPronouncingPracticeBlock block={block} locale={locale} />
  }

  if (template === 'multiple_choice') {
    return <LegacyMultipleChoiceBlock block={block} locale={locale} />
  }

  if (template === 'conversation') {
    return (
      <LegacyConversationTemplateBlock
        block={block}
        locale={locale}
        structureMode={false}
      />
    )
  }

  if (template === 'structure') {
    return (
      <LegacyConversationTemplateBlock
        block={block}
        locale={locale}
        structureMode={true}
      />
    )
  }

  return <LegacyFallbackBlock block={block} locale={locale} template={template} />
}

export function OnlineBookContentRenderer({
  blocks,
  locale,
}: OnlineBookContentRendererProps) {
  const { state } = useDashboardState()
  const accentDefault = state.myUser?.accent_default
  const orderedBlocks = useMemo(
    () => getOnlineBookBlocksInSourceOrder(selectPreferredLearningLessonDetails(blocks, accentDefault)),
    [accentDefault, blocks],
  )

  return (
    <div className="legacyOnlineBookContent">
      {orderedBlocks.map((block) => (
        <div key={block.id}>{renderBlock(block, locale)}</div>
      ))}
    </div>
  )
}

